import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useCountryCode, useLanguageCode } from '../../contexts/environment';
import { useDebouncedDataFetching } from '../../hooks/useDebouncedDataFetching';
import { useAppDispatch } from '../../store';
import { setAvailability } from '../../store/app';
import { getFormState } from '../../store/app/selectors';
import { getIcfState, getGlobalItemLists } from '../../store/icf/selectors';
import { getAllProducts } from '../../store/products/selectors';
import { SINGLE_WALL, CORNER_WALL } from '../../utils';
import { isEmpty } from '../../utils/isEmpty';
import LoadingIndicator from '../../views/LoadingIndicator';
import { ProductListProducts } from './ProductListProducts';
import { StyledContainer, StyledFlexColumn } from './styles';
/*
 * Filter out VPC codes with missing products
 */
const filterArticles = (articles, products) => {
    return articles.filter(article => {
        return article?.vpcData?.itemList?.every(art => {
            const product = products.find(product => product.id === art.id);
            return !!product;
        });
    });
};
export const ProductListRecommendations = ({ onReadMore, isMobile }) => {
    const countryCode = useCountryCode();
    const languageCode = useLanguageCode();
    const dispatch = useAppDispatch();
    const form = useSelector(getFormState);
    const products = useSelector(getAllProducts);
    const globalItemLists = useSelector(getGlobalItemLists);
    const { data, error, loading } = useSelector(getIcfState);
    const { fetchSingleWallData, fetchCornerWallData, updateGlobalItemLists, isDebouncing, abortPromises } = useDebouncedDataFetching();
    // Data fetching
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const fetchDataSingleWall = () => {
            const params = {
                width: form.size_width,
                height: form.size_height,
                depth: form.size_depth,
                door_type: form.door_type,
                country_code: countryCode,
                language_code: languageCode,
                include_signifiers: true,
                is_corner: false,
                storage_need: form.storage_type
            };
            fetchSingleWallData(params);
        };
        const fetchDataCornerWall = () => {
            const size = form.selectState?.size_depth_right?.selectedIndex === 1
                ? form.size_width_left_85
                : form.size_width_left_88;
            const size_adjacent = form.selectState?.size_depth_left?.selectedIndex === 1
                ? form.size_width_right_85
                : form.size_width_right_88;
            const params = {
                width: size,
                width_adjacent_frame: size_adjacent,
                height: form.size_height,
                depth: form.size_depth_left,
                depth_adjacent_frame: form.size_depth_right,
                door_type: form.door_type,
                country_code: countryCode,
                language_code: languageCode,
                include_signifiers: true,
                is_corner: true,
                storage_need: form.storage_type
            };
            fetchCornerWallData(params);
        };
        const isSingleFormValid = form.wall === SINGLE_WALL && form.size_width && form.size_height && form.size_depth;
        const isCornerFormValid = form.wall === CORNER_WALL &&
            (form.size_width_left_85 || form.size_width_left_88) &&
            (form.size_width_right_85 || form.size_width_right_88) &&
            form.size_depth_left &&
            form.size_depth_right;
        if (isSingleFormValid || isCornerFormValid) {
            // Abort any pending promise before making a new request
            abortPromises();
        }
        if (isSingleFormValid) {
            fetchDataSingleWall();
        }
        if (isCornerFormValid) {
            fetchDataCornerWall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        form.wall,
        form.door_type,
        form.size_depth,
        form.size_depth_left,
        form.size_depth_right,
        form.size_height,
        form.size_width,
        form.size_width_left_85,
        form.size_width_right_85,
        form.size_width_left_88,
        form.size_width_right_88,
        form.storage_type,
        fetchSingleWallData,
        fetchCornerWallData
    ]);
    // Update global item lists
    useEffect(() => {
        const orderedDesignList = data?.ordered_design_list || [];
        updateGlobalItemLists(products, form, orderedDesignList);
    }, [data?.ordered_design_list, form, products, updateGlobalItemLists]);
    // TODO: ugly, refactor
    useEffect(() => {
        const availabilities = [...globalItemLists]
            .flatMap(itemListItem => {
            return itemListItem.itemList.map(art => {
                const product = products.find(product => product.id === art.id)?.product;
                return product ? { vpc: itemListItem.vpc, product } : null;
            });
        })
            .filter((x) => Boolean(x))
            .reduce((acc, { vpc, product }) => {
            acc[vpc] = [...(acc[vpc] || []), product];
            return acc;
        }, {});
        dispatch(setAvailability(availabilities));
    }, [dispatch, globalItemLists, products]);
    const conditions = {
        loading: loading || error || isEmpty(data) || isDebouncing,
        success: !isEmpty(data?.ordered_design_list) && data?.success
    };
    if (conditions.loading) {
        return _jsx(LoadingIndicator, { width: 'auto' });
    }
    const articles = filterArticles(data?.ordered_design_list ?? [], products);
    return (_jsx(StyledFlexColumn, { children: _jsx(StyledContainer, { children: _jsx(ProductListProducts, { isMobile: isMobile, articles: loading ? [] : articles, onReadMore: onReadMore, isLoading: loading }) }, form.wall) }));
};
//# sourceMappingURL=ProductListRecommendations.js.map